import React, { ChangeEvent, useState } from 'react';
import { toast } from '../../../../../ui/toast';
import {
  CalendarClockIcons,
  ClockIcons,
  InfoIcons,
  NotebookIcons,
  SettingsIcons,
  TagIcons,
  TestNoteIcons,
} from '../../../../../icons/icons';
import { Button } from '../../../../../ui';
import { PencilIcon } from '../../../../../ui/icons/icons';
import { DatePicker } from '../../../../../ui/input/date_picker/date_picker';
import { TextArea } from '../../../../../ui/input/text_area/text_area';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { ToggleInput } from '../../../../../ui/input/toggle_input/toggle_input';
import {
  updateAssessment,
  updateAssessmentStatus,
} from '../../../../../utils/api_service/assessment_api';
import { Permission } from '../../../../../utils/helpers/permissions';
import PermissionGate from '../../../../../utils/hooks/PermissionGate';
import usePermissions from '../../../../../utils/hooks/usePermissions';
import {
  Assessment,
  BasicInfoInputs,
} from '../../../../../utils/types/assessment_types';

type AssessmentSummaryProps = {
  assessment: Assessment;
};

export const AssessmentSummary = ({ assessment }: AssessmentSummaryProps) => {
  const { hasPermissions } = usePermissions();
  const [assessmentFormInfo, setAssessmentFormInfo] = useState<BasicInfoInputs>(
    {
      title: assessment.assessmentTitle,
      description: assessment.description,
      startDate: new Date(assessment.startDate * 1000)
        .toISOString()
        .split('T')[0],
      endDate: new Date(assessment.endDate * 1000).toISOString().split('T')[0],
      type: assessment.assessmentType,
      jobPosition: assessment.jobPosition || undefined,
    },
  );
  const [active, setActive] = useState<boolean>(assessment.isActive);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setAssessmentFormInfo({
      ...assessmentFormInfo,
      [event.target.name]: event.target.value,
    });
  };

  const changeDate = (timeRange: any) => {
    setAssessmentFormInfo({
      ...assessmentFormInfo,
      startDate: timeRange.startDate,
      endDate: timeRange.endDate,
    });
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await updateAssessment(
        assessmentFormInfo,
        assessment.id,
      );
      if (response[0].id) {
        const statusResponse = await updateAssessmentStatus(
          assessment.id,
          active,
        );
        if (statusResponse.ok) {
          toast.success('Assessment Details Updated Successfully!', {
            position: 'bottom-center',
          });
        }
      }
    } catch (error) {
      toast.error('Failed to update assessment. Please try again.', {
        position: 'bottom-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Format dates for display
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className='min-h-screen bg-gray-50 p-6'>
      <div className='w-full mx-auto'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
          <div className='col-span-1'>
            <div className='bg-white rounded-3xl shadow-lg border border-gray-100 p-8 mb-6'>
              <div className='flex items-start justify-between mb-8'>
                <div className='flex-1'>
                  <div className='flex items-center gap-3 mb-3'>
                    <div className='w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-2xl flex items-center justify-center'>
                      <TestNoteIcons className='w-6 h-6 text-white' />
                    </div>
                    <div>
                      <h1 className='text-xl font-semibold text-gray-900'>
                        { assessment.assessmentTitle }
                      </h1>
                      <div className='flex items-center gap-2 mt-1'>
                        <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                        >
                          <div
                              className={`w-2 h-2 rounded-full mr-2 ${
                                active ? 'bg-green-500' : 'bg-red-500'
                              }`}
                          ></div>
                          { active ? 'Active' : 'Inactive' }
                        </span>
                        <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 capitalize'>
                          { assessment.assessmentType } Assessment
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              { /* Quick Stats */ }
              <div className='grid grid-cols-1 gap-6 mb-8'>
                <div className='bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-2xl p-6'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='text-emerald-600 text-sm font-medium mb-1'>
                        Start Date
                      </p>
                      <p className='text-emerald-900 text-lg font-semibold'>
                        { formatDate(assessment.startDate) }
                      </p>
                    </div>
                    <div className='w-12 h-12 bg-emerald-200 rounded-xl flex items-center justify-center'>
                      <CalendarClockIcons className='w-6 h-6 text-emerald-600' />
                    </div>
                  </div>
                </div>

                <div className='bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='text-blue-600 text-sm font-medium mb-1'>
                        End Date
                      </p>
                      <p className='text-blue-900 text-lg font-semibold'>
                        { formatDate(assessment.endDate) }
                      </p>
                    </div>
                    <div className='w-12 h-12 bg-blue-200 rounded-xl flex items-center justify-center'>
                      <CalendarClockIcons className='w-6 h-6 text-blue-600' />
                    </div>
                  </div>
                </div>

                <div className='bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='text-purple-600 text-sm font-medium mb-1'>
                        Assessment ID
                      </p>
                      <p className='text-purple-900 text-lg font-semibold'>
                        { assessment.id }
                      </p>
                    </div>
                    <div className='w-12 h-12 bg-purple-200 rounded-xl flex items-center justify-center'>
                      <TagIcons className='w-6 h-6 text-purple-600' />
                    </div>
                  </div>
                </div>
              </div>
              <div className='flex items-center gap-2 text-sm text-gray-500'>
                <ClockIcons className='w-4 h-4 text-gray-500' /> Last updated:{ ' ' }
                { new Date().toLocaleDateString() }
              </div>
            </div>
          </div>
          <div className='col-span-1 md:col-span-2'>
            { ' ' }
            <div className='bg-white rounded-3xl shadow-lg border border-gray-100 p-8'>
              <div className='flex items-center gap-3 mb-8'>
                <div className='w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center'>
                  <PencilIcon className='w-5 h-5 text-white' />
                </div>
                <h2 className='text-xl font-semibold text-gray-900'>
                  Assessment Configuration
                </h2>
              </div>

              <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                { /* Basic Information */ }
                <div className='space-y-6'>
                  <div className='bg-gray-50 rounded-2xl p-6'>
                    <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2'>

                      <InfoIcons className='w-5 h-5 text-blue-600' />
                      Basic Information
                    </h3>

                    <div className='space-y-4'>
                      <TextInput
                          label='Assessment Name'
                          name='title'
                          value={assessmentFormInfo.title}
                          type='text'
                          onChange={handleChange}
                          disabled={
                            !hasPermissions([Permission.ASSESSMENTS_EDIT])
                          }
                      />

                      { assessment.assessmentType === 'candidate' && (
                        <TextInput
                            label='Job Title'
                            name='jobPosition'
                            value={assessmentFormInfo.jobPosition || ''}
                            type='text'
                            onChange={handleChange}
                            disabled={
                              !hasPermissions([Permission.ASSESSMENTS_EDIT])
                            }
                        />
                      ) }
                    </div>
                  </div>
                </div>

                { /* Date Range */ }
                <div className='space-y-6'>
                  <div className='bg-gray-50 rounded-2xl p-6'>
                    <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2'>
                      <CalendarClockIcons className='w-5 h-5 text-emerald-600' />
                      Schedule
                    </h3>
                    <DatePicker
                        label='Assessment Time Range'
                        startDate={assessmentFormInfo.startDate}
                        endDate={assessmentFormInfo.endDate}
                        onChange={changeDate}
                        disabled={!hasPermissions([Permission.ASSESSMENTS_EDIT])}
                    />
                  </div>
                </div>

                { /* Description - Full Width */ }
                <div className='lg:col-span-2'>
                  <div className='bg-gray-50 rounded-2xl p-6'>
                    <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2'>
                      <NotebookIcons className='w-5 h-5 text-purple-600' />
                      Description
                    </h3>
                    <TextArea
                        label=''
                        name='description'
                        value={assessmentFormInfo.description}
                        onChange={handleChange}
                        disabled={!hasPermissions([Permission.ASSESSMENTS_EDIT])}
                    />
                  </div>
                </div>
              </div>

              { /* Status Section */ }
              <PermissionGate
                  requiredPermissions={[Permission.ASSESSMENTS_EDIT]}
              >
                <div className='mt-8 p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <h3 className='text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2'>
                        <SettingsIcons className='w-5 h-5 text-orange-600' />
                        Assessment Status
                      </h3>
                      <p className='text-gray-600 text-sm'>
                        Control whether this assessment is active and accepting
                        responses
                      </p>
                    </div>
                    <div className='ml-6'>
                      <ToggleInput
                          toggled={active}
                          handleToggle={() => setActive(!active)}
                          name='is_active'
                          informationText='Switch to make active/inactive'
                      />
                    </div>
                  </div>
                </div>

                { /* Action Buttons */ }
                <div className='flex items-center justify-end mt-8 pt-6 border-t border-gray-200'>
                  <div className='flex items-center gap-3'>
                    <Button
                        onClick={() => window.location.reload()}
                        variant='secondary'
                        label='Reset Changes'
                    />
                    <Button
                        onClick={handleSubmit}
                        variant='primary'
                        label={isLoading ? 'Saving...' : 'Save Changes'}
                        disabled={isLoading}
                    />
                  </div>
                </div>
              </PermissionGate>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
